/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

export default styled(Box)(({ theme, ownerState }) => {
  // Defensive check for undefined theme or missing properties
  if (!theme || !theme.palette || !theme.functions || !theme.borders || !theme.boxShadows) {
    console.warn("MDBox: Theme initialization not complete yet.");
    return {};
  }

  const { palette, functions, borders, boxShadows } = theme;

  // Another safety check for required nested properties
  if (!palette || !functions || !borders || !boxShadows) {
    console.warn("MDBox: Critical theme properties are undefined");
    return {};
  }

  const { variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow } =
    ownerState || {};

  // Safely access nested properties with fallbacks
  const gradients = palette.gradients || {};
  const grey = palette.grey || {};
  const white = palette.white || { main: "#ffffff" };

  // Check if linearGradient function exists
  const linearGradient = functions.linearGradient || ((c1, c2) => `linear-gradient(${c1}, ${c2})`);
  const radius = borders.borderRadius || {};
  const colored = boxShadows.colored || {};

  const greyColors = {
    "grey-100": grey[100] || "#f8f9fa",
    "grey-200": grey[200] || "#e9ecef",
    "grey-300": grey[300] || "#dee2e6",
    "grey-400": grey[400] || "#ced4da",
    "grey-500": grey[500] || "#adb5bd",
    "grey-600": grey[600] || "#6c757d",
    "grey-700": grey[700] || "#495057",
    "grey-800": grey[800] || "#343a40",
    "grey-900": grey[900] || "#212529",
  };

  const validGradients = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
    "gold",
  ];

  const validColors = [
    "gold",
    "transparent",
    "white",
    "black",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "text",
    "grey-100",
    "grey-200",
    "grey-300",
    "grey-400",
    "grey-500",
    "grey-600",
    "grey-700",
    "grey-800",
    "grey-900",
  ];

  const validBorderRadius = ["xs", "sm", "md", "lg", "xl", "xxl", "section"];
  const validBoxShadows = ["xs", "sm", "md", "lg", "xl", "xxl", "inset"];

  // background value
  let backgroundValue = bgColor || "transparent";

  if (variant === "gradient") {
    // Additional safety check for gradient access
    if (
      validGradients.find((el) => el === bgColor) &&
      gradients[bgColor] &&
      gradients[bgColor].main &&
      gradients[bgColor].state
    ) {
      backgroundValue = linearGradient(gradients[bgColor].main, gradients[bgColor].state);
    } else {
      // Fallback if gradient isn't available
      backgroundValue = white.main || "#ffffff";
    }
  } else if (validColors.find((el) => el === bgColor)) {
    // Handle valid colors with proper fallbacks
    if (palette[bgColor] && palette[bgColor].main) {
      backgroundValue = palette[bgColor].main;
    } else if (greyColors[bgColor]) {
      backgroundValue = greyColors[bgColor];
    } else {
      backgroundValue = bgColor || "transparent";
    }
  } else {
    // Use bgColor directly if it's not in our valid colors list
    backgroundValue = bgColor || "transparent";
  }

  // color value
  let colorValue = color || "inherit";

  if (validColors.find((el) => el === color)) {
    if (palette[color] && palette[color].main) {
      colorValue = palette[color].main;
    } else if (greyColors[color]) {
      colorValue = greyColors[color];
    }
  }

  // borderRadius value
  let borderRadiusValue = borderRadius || 0;

  if (validBorderRadius.find((el) => el === borderRadius) && radius[borderRadius]) {
    borderRadiusValue = radius[borderRadius];
  }

  // boxShadow value
  let boxShadowValue = "none";

  if (validBoxShadows.find((el) => el === shadow) && boxShadows[shadow]) {
    boxShadowValue = boxShadows[shadow] || "none";
  } else if (coloredShadow && colored[coloredShadow]) {
    boxShadowValue = colored[coloredShadow] || "none";
  }

  return {
    opacity,
    background: backgroundValue,
    color: colorValue,
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
  };
});
